import * as React from 'react';

import { getAnalytics } from '../../utils/getAnaltyics';
import * as Utils from '../../utils/segmentUtils';

type SegmentOpts = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  integrations?: any;
  anonymousId?: string;
  context?: Record<string, unknown>;
};

type SharedSegmentParams = {
  properties?: Record<string, unknown>;
  options?: SegmentOpts;
  callback?: () => void;
};

export type SegmentEventParams = {
  eventName: string;
} & SharedSegmentParams;

export type SegmentPageParams = {
  categoryName?: string;
  name?: string;
} & SharedSegmentParams;

export type SegmentGroupParams = {
  groupId: string;
  traits?: Record<string, unknown>;
} & Pick<SharedSegmentParams, 'options' | 'callback'>;

export type SegmentAliasParams = {
  userId: string;
  previousId?: string;
} & Pick<SharedSegmentParams, 'options' | 'callback'>;

export type SegmentEventFn = (options: SegmentEventParams) => void;
export type SegmentPageFn = (options?: SegmentPageParams) => void;
export type SegmentGroupFn = (options: SegmentGroupParams) => void;
export type SegmentAliasFn = (options: SegmentAliasParams) => void;

export type UseSegmentHook = () => {
  track: SegmentEventFn;
  page: SegmentPageFn;
  group: SegmentGroupFn;
  alias: SegmentAliasFn;
};

export type ModelSegmentTrackEventParams = (segmentOptions: {
  analyticsTrackParams: SegmentEventParams;
  analyticsTrackFn: SegmentAnalytics.AnalyticsJS['track'];
}) => void;

export type ModelSegmentPageEventParams = (segmentOptions: {
  analyticsPageParams?: SegmentPageParams;
  analyticsPageFn: SegmentAnalytics.AnalyticsJS['page'];
}) => void;

export type ModelSegmentGroupEventParams = (segmentOptions: {
  analyticsGroupParams: SegmentGroupParams;
  analyticsGroupFn: SegmentAnalytics.AnalyticsJS['group'];
}) => void;

export type ModelSegmentAliasEventParams = (segmentOptions: {
  analyticsAliasParams: SegmentAliasParams;
  analyticsAliasFn: SegmentAnalytics.AnalyticsJS['alias'];
}) => void;

export const useSegment: UseSegmentHook = () => {
  const analytics = getAnalytics();

  const track: SegmentEventFn = (segmentOptions) => {
    if (analytics?.track) {
      Utils.modelSegmentTrackEventParams({
        analyticsTrackParams: segmentOptions,
        analyticsTrackFn: analytics.track,
      });
    }
  };

  const page: SegmentPageFn = (segmentOptions) => {
    if (analytics?.page) {
      Utils.modelSegmentPageEventParams({
        analyticsPageParams: segmentOptions,
        analyticsPageFn: analytics.page,
      });
    }
  };

  const group: SegmentGroupFn = (segmentOptions) => {
    if (analytics?.group) {
      Utils.modelSegmentGroupEventParams({
        analyticsGroupParams: segmentOptions,
        analyticsGroupFn: analytics.group,
      });
    }
  };

  const alias: SegmentAliasFn = (segmentOptions) => {
    if (analytics?.alias) {
      Utils.modelSegmentAliasEventParams({
        analyticsAliasParams: segmentOptions,
        analyticsAliasFn: analytics.alias,
      });
    }
  };

  /**
   * Memoizing to avoid recomputing
   * the return value/overall useSegment config if a rerender happens
   */
  return React.useMemo(() => {
    return {
      alias,
      group,
      page,
      track,
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [!!analytics]);
};
